import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { usePageContext, useSelectedIds } from "@ryde/redux";
import {
  addUserAndAssistantPlaceholder,
  appendChunkToLastAssistantMessage,
  applyFallbackResponseToLastAssistant,
  IAIQuestionRequestPayload,
  IAIQuestionResponse,
  IAiStreamCallbacks,
  IChatHistoryItem,
  RydeUiUnit,
  setElementsOnLastAssistantMessage,
  setReasoningOnLastAssistantMessage,
  SimpleResult,
  TAiUiElement,
} from "@ryde/shared";

export interface IUseBulkAiAdvisorSearchParams {
  questionStream: (
    payload: IAIQuestionRequestPayload,
    callbacks: IAiStreamCallbacks,
  ) => Promise<void>;
  question: (
    payload: IAIQuestionRequestPayload,
  ) => Promise<SimpleResult<IAIQuestionResponse> | undefined>;
  useRegularQuestionApiAsFallback?: boolean;
  /**
   * Who the question is about. Both default to the page underneath — its entity and its current row
   * selection. A caller that is not a page passes them itself (the ride investigation overlay is
   * mounted at the app root and always asks about its one ride).
   */
  entity?: RydeUiUnit;
  ids?: number[];
}

export const useBulkAiAdvisorChat = ({
  questionStream,
  question,
  useRegularQuestionApiAsFallback,
  entity,
  ids,
}: IUseBulkAiAdvisorSearchParams) => {
  const { entity: pageEntity } = usePageContext();
  const { selectedRowsIds } = useSelectedIds();
  const { t } = useTranslation();

  const [chatHistory, setChatHistory] = useState<IChatHistoryItem[]>([]);
  const [userMessage, setUserMessage] = useState("");
  const [sessionId, setSessionId] = useState<string>("");
  const [messageIndexCounter, setMessageIndexCounter] = useState(0);
  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

  /**
   * Send one message and stream the answer.
   *
   * Takes the text explicitly rather than reading the input box, because a tapped
   * assistant option goes through this same path. Keeping clicks indistinguishable from
   * typing is deliberate: the agent's confirmation gate keys off an explicit affirmative
   * in the user's turn, so a "Yes, cancel it" button needs no special handling anywhere.
   */
  const submitMessage = useEvent(async (rawMessage: string): Promise<void> => {
    const currentUserMessage = rawMessage.trim();
    if (!currentUserMessage) {
      return;
    }
    const payload: IAIQuestionRequestPayload = {
      entity: entity ?? pageEntity,
      sessionId,
      ids: ids ?? selectedRowsIds,
      question: currentUserMessage,
    };

    const assistantMessageIndex = messageIndexCounter + 1;
    setMessageIndexCounter(prev => prev + 2);
    setUserMessage("");
    textFieldRef.current?.focus();

    setChatHistory(prev =>
      addUserAndAssistantPlaceholder(
        prev,
        currentUserMessage,
        messageIndexCounter,
        assistantMessageIndex,
      ),
    );

    let hasReceivedChunks = false;
    const streamCallbacks = {
      onSession: (newSessionId: string) => setSessionId(newSessionId),
      onChunk: (text: string) => {
        hasReceivedChunks = true;
        setChatHistory(prev => appendChunkToLastAssistantMessage(prev, text));
      },
      onElements: (elements: TAiUiElement[]) => {
        setChatHistory(prev =>
          setElementsOnLastAssistantMessage(prev, elements),
        );
      },
      onDone: (reasoning?: string) => {
        if (reasoning) {
          setChatHistory(prev =>
            setReasoningOnLastAssistantMessage(prev, reasoning),
          );
        }
      },
      onError: async (_message: string) => {
        if (hasReceivedChunks) {
          return;
        }

        let response: SimpleResult<IAIQuestionResponse> | undefined;
        if (useRegularQuestionApiAsFallback) {
          response = await question(payload);
          if (response?.success && response?.data?.sessionId) {
            setSessionId(response.data.sessionId);
          }
        }

        const fallbackErrorText = t(
          "aiAdvisor.streamError",
          "Something went wrong. Please try again.",
        );
        setChatHistory(prev =>
          applyFallbackResponseToLastAssistant(
            prev,
            response,
            fallbackErrorText,
          ),
        );
      },
    };

    await questionStream(payload, streamCallbacks);
    // Again after the turn, not only before it. Sending moves focus to the arrow button —
    // or, for a tapped option, to a control that has just been re-rendered out of the DOM —
    // and typing the next question should never require reaching for the mouse first.
    textFieldRef.current?.focus();
  });

  const searchApply = useEvent(async (): Promise<void> =>
    submitMessage(userMessage),
  );

  /** Send a tapped option's value. Identical to the user typing it. */
  const sendElementValue = useEvent(async (value: string): Promise<void> =>
    submitMessage(value),
  );

  const handleMessageChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
      setUserMessage(event.target.value);
    },
    [],
  );

  return {
    chatHistory,
    userMessage,
    sessionId,
    textFieldRef,
    messageIndexCounter,
    setSessionId,
    searchApply,
    sendElementValue,
    setUserMessage,
    setChatHistory,
    handleMessageChange,
  };
};
